import { COMPONENT_TYPE } from 'common/constants';
import type { StoryDictionaryMap, SupportedLanguage } from 'common/i18n/types';
import type {
	BBModel,
	BBModelTextChild,
	CardData,
	ComponentTypes,
	FieldEditorProps,
	StoryDefaultPlatform,
} from 'types/story';

export type CommonCreationContext = {
	defaultPlatform: StoryDefaultPlatform;
};

type Localized = {
	dictionary: StoryDictionaryMap;
};

type Language = {
	language: SupportedLanguage;
};

type Card = {
	cardType: CardData['type'];
};

type ContextOverrides = {
	[COMPONENT_TYPE.BUTTON]: Localized;
	[COMPONENT_TYPE.BUTTON_SUBMIT]: Localized;
	[COMPONENT_TYPE.CHECKBOX]: Localized;
	[COMPONENT_TYPE.FILE_UPLOAD_FIELD]: Localized;
	[COMPONENT_TYPE.GOOGLE_LOGIN]: Localized;
	[COMPONENT_TYPE.SEARCH_FIELD]: Localized;
	[COMPONENT_TYPE.SELECT]: Localized;
	[COMPONENT_TYPE.SHARE]: Localized;
	[COMPONENT_TYPE.SMS_LOGIN]: Localized;
	[COMPONENT_TYPE.TEXT]: Localized;
	[COMPONENT_TYPE.TEXT_AREA_FIELD]: Localized;
	[COMPONENT_TYPE.TEXT_FIELD]: Localized;
	[COMPONENT_TYPE.SLIDER]: Language;
	[COMPONENT_TYPE.SLIDER_ARROW]: Language;
	[COMPONENT_TYPE.ANSWER]: Card;
};

type ContextRequirement<T extends ComponentTypes> = T extends keyof ContextOverrides
	? ContextOverrides[T]
	: Record<never, never>;

type UnionToIntersection<T extends object> = (T extends object ? (context: T) => void : never) extends (
	context: infer TIntersection
) => void
	? TIntersection
	: never;

export type CreationContextFor<T extends ComponentTypes> = CommonCreationContext &
	UnionToIntersection<ContextRequirement<T>> &
	Partial<Localized & Language & Card>;

type TextChildren = BBModelTextChild;
type LeafChildren = [];
type ArrayChildren = BBModel[];

/**
 * Recipe children are one of three shapes. Classify a new COMPONENT_TYPE here;
 * `ChildrenOf` is derived from these lists.
 *
 * Do not infer "can nest blocks" from `Array.isArray(children)`: LeafChildren is `[]`.
 * Do not use `editorProps.expandable` as the classifier: that is the Layers UI flag
 * (recipes set it true for ArrayChildren) and hydrated JSON may omit it.
 */
export const TEXT_CHILD_TYPES = [
	COMPONENT_TYPE.TEXT,
	COMPONENT_TYPE.BUTTON,
	COMPONENT_TYPE.BUTTON_SUBMIT,
	COMPONENT_TYPE.CHECKBOX,
	COMPONENT_TYPE.SHARE,
	COMPONENT_TYPE.GOOGLE_LOGIN,
	COMPONENT_TYPE.SMS_LOGIN,
] as const;

// children is array of never
export const LEAF_CHILD_TYPES = [
	COMPONENT_TYPE.CARD,
	COMPONENT_TYPE.IMG,
	COMPONENT_TYPE.ICON,
	COMPONENT_TYPE.LOTTIE,
	COMPONENT_TYPE.VISUAL_COMPARE,
	COMPONENT_TYPE.AD_UNIT,
	COMPONENT_TYPE.COUNTER,
	COMPONENT_TYPE.EMBED,
	COMPONENT_TYPE.INFOGRAPHIC,
	COMPONENT_TYPE.SHAPE,
	COMPONENT_TYPE.RESULT_SHAPE,
	COMPONENT_TYPE.RESULT_TEXT,
	COMPONENT_TYPE.TIMER,
	COMPONENT_TYPE.VIDEO,
	COMPONENT_TYPE.FILE_UPLOAD_FIELD,
	COMPONENT_TYPE.SEARCH_FIELD,
	COMPONENT_TYPE.SELECT,
	COMPONENT_TYPE.TEXT_FIELD,
	COMPONENT_TYPE.TEXT_AREA_FIELD,
	COMPONENT_TYPE.SLIDER_PAGINATION,
	COMPONENT_TYPE.ANSWER_INPUT,
	COMPONENT_TYPE.ANSWER_PROGRESS,
] as const;

// children is array of BBModel
export const ARRAY_CHILD_TYPES = [
	COMPONENT_TYPE.BOX,
	COMPONENT_TYPE.CONTENT,
	COMPONENT_TYPE.FLOAT_ABOVE,
	COMPONENT_TYPE.FLOAT_BELOW,
	COMPONENT_TYPE.OVERLAY,
	COMPONENT_TYPE.SWIPE,
	COMPONENT_TYPE.SORTABLE_BOX,
	COMPONENT_TYPE.CMS_REPEATER,
	COMPONENT_TYPE.SLIDER,
	COMPONENT_TYPE.SLIDER_ARROW,
	COMPONENT_TYPE.SLIDER_SLIDES,
	COMPONENT_TYPE.ANSWER,
] as const;

export type TextChildType = (typeof TEXT_CHILD_TYPES)[number];
export type LeafChildType = (typeof LEAF_CHILD_TYPES)[number];
export type ArrayChildType = (typeof ARRAY_CHILD_TYPES)[number];

function isListedType<T extends ComponentTypes>(list: readonly T[], type: ComponentTypes): type is T {
	return (list as readonly ComponentTypes[]).includes(type);
}

export function hasTextChildren(type: ComponentTypes): type is TextChildType {
	return isListedType(TEXT_CHILD_TYPES, type);
}

export function hasLeafChildren(type: ComponentTypes): type is LeafChildType {
	return isListedType(LEAF_CHILD_TYPES, type);
}

export function hasArrayChildren(type: ComponentTypes): type is ArrayChildType {
	return isListedType(ARRAY_CHILD_TYPES, type);
}

export type ChildrenOf = {
	[T in TextChildType]: TextChildren;
} & {
	[T in LeafChildType]: LeafChildren;
} & {
	[T in ArrayChildType]: ArrayChildren;
};

type FieldEditor = FieldEditorProps;
type DefaultEditor = {};

export type EditorOf = {
	[COMPONENT_TYPE.TEXT_FIELD]: FieldEditor;
	[COMPONENT_TYPE.TEXT_AREA_FIELD]: FieldEditor;
	[COMPONENT_TYPE.CHECKBOX]: FieldEditor;
	[COMPONENT_TYPE.SELECT]: FieldEditor;
	[COMPONENT_TYPE.FILE_UPLOAD_FIELD]: FieldEditor;
} & {
	[T in Exclude<
		ComponentTypes,
		| typeof COMPONENT_TYPE.TEXT_FIELD
		| typeof COMPONENT_TYPE.TEXT_AREA_FIELD
		| typeof COMPONENT_TYPE.CHECKBOX
		| typeof COMPONENT_TYPE.SELECT
		| typeof COMPONENT_TYPE.FILE_UPLOAD_FIELD
	>]: DefaultEditor;
};

export type CreatedBuildingBlock<T extends ComponentTypes> = BBModel<T, ChildrenOf[T], EditorOf[T]>;
